<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref} from "vue";
import useSharedMethod from "@/use/SharedMethod.js";
import {usePersonalizedVariableStore} from "@/stores/Edms/PersonalizedVariable.js";
import useSharedSelectData from "@/use/SharedSelectData";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        archiveAction: Boolean,
        unarchiveAction: Boolean,
        type: String,
    },

    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay} = useSharedData();
        const {$route, $refs} = useTools();
        const {getBack} = useSharedMethod();
        const {documentModules, personalizedVariableTypes} = useSharedSelectData();

        const personalizedVariableStore = usePersonalizedVariableStore();

        const readonly = computed(() => props.viewAction || props.deleteAction);

        const personalizedVariable = ref({
            activateNamedValue: false,
            required: false,
            isPaySection: false,
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/personalizedVariables/${id}`).then((response) => {
                personalizedVariable.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/personalizedVariables', personalizedVariable.value).then((response) => {
                    personalizedVariableStore.addPersonalizedVariable(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/personalizedVariables', personalizedVariable.value).then((response) => {
                    personalizedVariableStore.updatePersonalizedVariable(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/personalizedVariables/${personalizedVariable.value.id}`).then(() => {
                personalizedVariableStore.deletePersonalizedVariable(personalizedVariable.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });

        return {
            personalizedVariable,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            documentModules,
            personalizedVariableTypes,
            save,
            update,
            remove,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        persistent
        scrollable
        max-width="600"
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            />
            <v-card-text style="background-color: #FAFAFA;">
                <v-form lazy-validation ref="form">
                    <v-container fluid>
                        <w-fieldset>
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.variable')" required/>
                                    <w-text-field
                                        v-model="personalizedVariable.name"
                                        auto-focus
                                        prefix="[#"
                                        suffix="#]"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.module')" required/>
                                    <w-enum-select
                                        v-model="personalizedVariable.module"
                                        :items="documentModules"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.type')" required/>
                                    <w-enum-select
                                        v-model="personalizedVariable.type"
                                        :items="personalizedVariableTypes"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-checkbox
                                        v-model="personalizedVariable.activateNamedValue"
                                        :label="$t('common.enable_value_name')"
                                        :readonly="readonly"
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" v-if="personalizedVariable.activateNamedValue">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="personalizedVariable.namedValue"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-checkbox
                                        v-model="personalizedVariable.required"
                                        :label="$t('common.required')"
                                        :readonly="readonly"
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-checkbox
                                        v-model="personalizedVariable.isPaySection"
                                        :label="$t('common.is_pay_section')"
                                        :readonly="readonly"
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="12" v-if="personalizedVariable.isPaySection">
                                    <w-label :label="$t('pay_slip.pay_section')" required/>
                                    <text-field-pay-section
                                        v-model="personalizedVariable.paySectionCode"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>